// ─── Axios Instance & Interceptors ───────────────────────────────────────────
// Handles: base config, auth headers, 401 → refresh → retry logic
// Refresh token is HTTP-only cookie — no manual handling needed for sending it

import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import { APIErrorResponse, RefreshResponse } from '../types/auth.types';

// ─── Config ───────────────────────────────────────────────────────────────────

// const API_BASE_URL = 'http://127.0.0.1:8000/api/v1';
const API_BASE_URL = 'https://onetech.binbyte.tech/api/v1';
// const API_BASE_URL = 'https://one.binbyte.co.ke/api/v1';
// https://onetech.binbyte.tech

const REFRESH_ENDPOINT = '/auth/token/refresh/';

// ─── Axios Instance ───────────────────────────────────────────────────────────

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Required for HTTP-only refresh token cookie
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 15_000,
});

// ─── Token Accessor (set by authStore) ───────────────────────────────────────
// We use a getter function to avoid circular imports with the store

let getAccessToken: () => string | null = () => null;
let onRefreshSuccess: (token: string) => void = () => {};
let onRefreshFailure: () => void = () => {};

export function configureApiAuth(opts: {
  getToken: () => string | null;
  onSuccess: (token: string) => void;
  onFailure: () => void;
}) {
  getAccessToken = opts.getToken;
  onRefreshSuccess = opts.onSuccess;
  onRefreshFailure = opts.onFailure;
}

// ─── Request Interceptor — Attach Bearer Token ────────────────────────────────

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token && config.headers) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// ─── Refresh Queue (prevent simultaneous 401 storms) ─────────────────────────

let isRefreshing = false;
let refreshQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

function processQueue(token: string | null, error: unknown = null) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (token) resolve(token);
    else reject(error);
  });
  refreshQueue = [];
}

// ─── Response Interceptor — 401 Refresh & Retry ───────────────────────────────

apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
    // Only handle 401 on non-refresh endpoints
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      originalRequest.url !== REFRESH_ENDPOINT
    ) {
      if (isRefreshing) {
        // Queue this request until refresh completes
        return new Promise((resolve, reject) => {
          refreshQueue.push({
            resolve: (token: string) => {
              if (originalRequest.headers) {
                originalRequest.headers['Authorization'] = `Bearer ${token}`;
              }
              resolve(apiClient(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await apiClient.post<RefreshResponse>(REFRESH_ENDPOINT);
        const newToken = (data as any).data.access;

        onRefreshSuccess(newToken);
        processQueue(newToken);

        if (originalRequest.headers) {
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        }

        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(null, refreshError);
        onRefreshFailure();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ─── Error Parser ─────────────────────────────────────────────────────────────

export function parseAPIError(error: unknown): APIErrorResponse {
  if (axios.isAxiosError(error) && error.response?.data) {
    const data = error.response.data;
    if (data.success === false && data.error) {
      return { ...data, status: error.response.status };
    }
    // Fallback for non-envelope errors (DRF default)
    return {
      success: false,
      error: {
        code: 'API_ERROR',
        message: data.detail ?? data.message ?? 'An unexpected error occurred.',
        details: data.errors ?? undefined,
      },
      status: error.response.status,
    };
  }

  if (error instanceof Error) {
    if (error.message === 'Network Error') {
      return {
        success: false,
        error: { code: 'NETWORK_ERROR', message: 'Network error. Please check your connection.' },
        status: 0,
      };
    }
  }

  return {
    success: false,
    error: { code: 'UNKNOWN_ERROR', message: 'An unexpected error occurred.' },
    status: 0,
  };
}

export default apiClient;